import type { MonitorFlagKind } from '../../shared/protocol.ts';
import { realtime } from './realtime.ts';

/**
 * Grabador de la pantalla del jugador en las salas supervisadas.
 *
 * Solo registra lo que ocurre DENTRO de la pestaña de Open Dahu (clics, cambios
 * en la interfaz, scroll); no ve otras pestañas, otras apps ni la cámara. Los
 * campos de texto se enmascaran. Los eventos viajan por el WebSocket de la sala
 * al servidor, que los reenvía al anfitrión y los guarda al terminar.
 *
 * rrweb se carga bajo demanda: quien no juega en una sala supervisada no lo
 * descarga.
 */

const FLUSH_MS = 1000;
/** Tamaño máximo de un lote (caracteres de JSON); el servidor acepta hasta 4 MB. */
const MAX_BATCH_CHARS = 600_000;
const MAX_BATCH_EVENTS = 300;
/** Un evento más grande que esto se descarta en vez de bloquear el canal. */
const MAX_EVENT_CHARS = 3_000_000;
/** Sin conexión se acumula hasta aquí; pasado esto se descarta y se reinicia con un snapshot. */
const MAX_OFFLINE_EVENTS = 3000;

interface Queued {
  event: unknown;
  size: number;
}

/**
 * Por dónde salen los eventos. Las salas usan su WebSocket (el valor por
 * defecto); los exámenes, que no tienen sala, los mandan por HTTP.
 */
export interface RecorderTransport {
  online: () => boolean;
  sendEvents: (events: unknown[]) => void;
  sendFlag: (kind: MonitorFlagKind) => void;
}

const roomTransport: RecorderTransport = {
  online: () => realtime.status === 'online',
  sendEvents: (events) => realtime.send({ t: 'rec:events', events }),
  sendFlag: (kind) => realtime.send({ t: 'rec:flag', kind }),
};

/**
 * Vigila las señales de posible trampa: salir de la pestaña, perder el foco,
 * copiar y pegar. Devuelve la función que deja de vigilar.
 */
export function watchCheatSignals(flag: (kind: MonitorFlagKind) => void): () => void {
  let stopped = false;
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flag('hidden');
  };
  // Perder el foco sin ocultar la pestaña (otra ventana encima, pantalla
  // dividida). Se espera un instante: cambiar de pestaña dispara también `blur`
  // y ya se cuenta como "hidden".
  const onBlur = () => {
    window.setTimeout(() => {
      if (!stopped && document.visibilityState === 'visible' && !document.hasFocus()) flag('blur');
    }, 400);
  };
  const onPaste = () => flag('paste');
  const onCopy = () => flag('copy');

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('blur', onBlur);
  document.addEventListener('paste', onPaste);
  document.addEventListener('copy', onCopy);
  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('blur', onBlur);
    document.removeEventListener('paste', onPaste);
    document.removeEventListener('copy', onCopy);
  };
}

export function startRecording(transport: RecorderTransport = roomTransport): () => void {
  let stopped = false;
  let stopRecord: (() => void) | undefined;
  let rr: typeof import('rrweb') | null = null;
  let buffer: Queued[] = [];
  let needSnapshot = false;
  let timer: number | undefined;

  const flush = () => {
    if (stopped || !rr) return;

    if (!transport.online()) {
      if (buffer.length > MAX_OFFLINE_EVENTS) {
        buffer = [];
        needSnapshot = true;
      }
      return;
    }
    if (needSnapshot) {
      needSnapshot = false;
      buffer = [];
      // Tras una caída larga se empieza de cero con una foto completa.
      rr.record.takeFullSnapshot(true);
    }

    while (buffer.length > 0) {
      const batch: unknown[] = [];
      let chars = 0;
      while (buffer.length > 0 && batch.length < MAX_BATCH_EVENTS) {
        const next = buffer[0];
        if (batch.length > 0 && chars + next.size > MAX_BATCH_CHARS) break;
        batch.push(next.event);
        chars += next.size;
        buffer.shift();
      }
      transport.sendEvents(batch);
    }
  };

  const flag = (kind: MonitorFlagKind) => {
    transport.sendFlag(kind);
    // Queda también en la línea de tiempo de la grabación.
    rr?.record.addCustomEvent(kind, {});
  };
  const stopWatching = watchCheatSignals(flag);

  void import('rrweb')
    .then((mod) => {
      if (stopped) return;
      rr = mod;
      stopRecord = mod.record({
        emit(event) {
          const size = JSON.stringify(event).length;
          if (size > MAX_EVENT_CHARS) return;
          buffer.push({ event, size });
        },
        maskAllInputs: true,
        checkoutEveryNms: 60_000,
        sampling: { mousemove: 150, scroll: 200, input: 'last' },
        recordCanvas: false,
        collectFonts: false,
      });
      timer = window.setInterval(flush, FLUSH_MS);
    })
    .catch(() => {
      /* sin grabador (bloqueador de scripts, red): la partida sigue igual */
    });

  return () => {
    if (stopped) return;
    flush();
    stopped = true;
    if (timer) window.clearInterval(timer);
    stopRecord?.();
    stopWatching();
  };
}
